/**
 * StandaloneWaiverPage — walk-up waiver signing without a booking (/sign/{org}).
 * Thin wrapper around the shared WaiverFlow (same flow as the customer ticket).
 * After signing, offers "Sign Another Waiver" so the device is kiosk-ready.
 */
import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Shield } from 'lucide-react';
import WaiverFlow from '../components/WaiverFlow';
import api from '../utils/api';

export default function StandaloneWaiverPage() {
  const { orgSlug } = useParams();
  const [org, setOrg] = useState(null);
  const [loadingOrg, setLoadingOrg] = useState(true);
  const [loadError, setLoadError] = useState('');

  useEffect(() => {
    api.get(`/v2/public/org/${orgSlug}/waiver`)
      .then((r) => { setOrg(r.data); setLoadingOrg(false); })
      .catch(() => { setLoadError('Waiver not available or not configured for this organisation.'); setLoadingOrg(false); });
  }, [orgSlug]);

  const handleSubmit = async (p) => {
    await api.post(`/v2/public/org/${orgSlug}/sign-waiver`, {
      signerName: p.signerName,
      signatureData: p.signatureData,
      verified: p.verified,
      verificationMethod: p.verificationMethod,
      verifiedContact: p.verifiedContact,
      phoneNumber: p.phoneNumber,
      email: p.email,
      deviceInfo: p.deviceInfo,
      idVerificationRequired: p.idVerificationRequired || undefined,
      signedAt: p.signedAt,
      waiverVersion: org?.waiverVersion || '1.0',
      ageRange: p.ageRange,
      marketingOptIn: p.marketingOptIn,
      marketingConsentText: p.marketingConsentText,
      smsMarketingOptIn: p.smsMarketingOptIn,
      smsMarketingConsentText: p.smsMarketingConsentText,
    });
  };

  if (loadingOrg) return (
    <div className="min-h-screen flex items-center justify-center">
      <div className="w-8 h-8 border-4 border-blue-600 border-t-transparent rounded-full animate-spin" />
    </div>
  );

  if (loadError) return (
    <div className="min-h-screen flex items-center justify-center p-4 text-center">
      <div className="max-w-sm">
        <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
          <Shield className="w-8 h-8 text-gray-400" />
        </div>
        <p className="text-gray-700 font-medium mb-1">Waiver not available</p>
        <p className="text-gray-500 text-sm mb-4">{loadError}</p>
        <Link to="/" className="text-sm text-blue-600 hover:underline">← BookingFlows</Link>
      </div>
    </div>
  );

  return (
    <WaiverFlow
      layout="page"
      waiverText={org.waiverText}
      branding={{
        name: org.orgName,
        logoUrl: org.logoUrl,
        brandColor: org.primaryColor || '#2563EB',
        waiverVersion: org.waiverVersion,
      }}
      onSubmit={handleSubmit}
      successAction="reset"
      region={
        ['uae', 'united arab emirates', 'ae'].includes((org.country || '').trim().toLowerCase()) ||
        (org.currency || '').trim().toUpperCase() === 'AED'
          ? 'UAE' : 'USA'
      }
    />
  );
}
